import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert, Box, Stack, FormControlLabel, Checkbox } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useState } from "react";
import AutocompleteInputOption from "../../models/autocompleteInputOption";
import { Order } from "../../models/order";

interface Props {
    open: boolean;
    onClose: () => void;
    order: Order;
    newService: AutocompleteInputOption;
    originalService: AutocompleteInputOption | undefined;
    onConfirm: (copyFromServiceId: number, overwriteTestMaterials: boolean) => void;
    onCancel: () => void;
}

export default function ServiceChangeDialog({ 
    open, 
    onClose, 
    order, 
    newService, 
    originalService,
    onConfirm,
    onCancel
}: Props) {
    const [overwriteTestMaterials, setOverwriteTestMaterials] = useState(false);

    // Użyj nazwy z oryginalnej usługi, lub z zamówienia jako fallback
    const originalServiceName = originalService?.name || order.serviceName || 'nieznana';

    const handleConfirm = () => {
        onConfirm(newService.id, overwriteTestMaterials);
        onClose();
    };

    const handleCancel = () => {
        onCancel();
        onClose();
    };

    // Logika dostępności testów dla nowej usługi
    const renderNewServiceAvailability = () => {
        const hasGraphicalTest = !!newService.packageTestPath;
        const hasGraphicalKey = !!newService.packageTestKeyPath;
        
        // Sprawdź czy questionsJson ma faktycznie pytania (nie jest pustą tablicą/obiektem/stringiem)
        const hasQuestions = (() => {
            const q = newService.questionsJson;
            if (!q) return false;
            if (Array.isArray(q)) return q.length > 0;
            if (typeof q === 'string') {
                const trimmed = (q as string).trim();
                return trimmed !== '' && trimmed !== '[]' && trimmed !== '{}';
            }
            if (typeof q === 'object') return Object.keys(q).length > 0;
            return false;
        })();

        // Komponent dla testów graficznych
        const graphicalTestsComponent = (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Testy graficzne:
                </Typography>
                <Stack spacing={0.5} sx={{ ml: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hasGraphicalTest ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                        ) : (
                            <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                        )}
                        <Typography variant="body2">
                            Arkusz: {hasGraphicalTest ? "✓ Dostępny" : "✗ Niedostępny"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hasGraphicalKey ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                        ) : (
                            <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                        )}
                        <Typography variant="body2">
                            Klucz: {hasGraphicalKey ? "✓ Dostępny" : "✗ Niedostępny"}
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        );

        // Komponent dla testów tekstowych
        const textualTestsComponent = (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Testy tekstowe:
                </Typography>
                <Stack spacing={0.5} sx={{ ml: 2 }}>
                    {hasQuestions ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            <Typography variant="body2">
                                Arkusz i klucz: ✓ Dostępne
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                            <Typography variant="body2">
                                Arkusz i klucz: ✗ Niedostępne
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Box>
        );

        return (
            <Box sx={{ mt: 2 }}>
                {/* Informacja o rodzaju testu */}
                <Box sx={{ 
                    backgroundColor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5, 
                    p: 2, 
                    mb: 3 
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Rodzaj testu:
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 700,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 0.75,
                                backgroundColor: newService.isGraphical ? 'info.lighter' : 'warning.lighter',
                                color: newService.isGraphical ? 'info.dark' : 'warning.dark'
                            }}
                        >
                            {newService.isGraphical ? "Graficzny" : "Tekstowy"}
                        </Typography>
                        <ArrowForwardIcon sx={{ color: 'text.secondary' }} />
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 700,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 0.75,
                                backgroundColor: newService.isGraphical ? 'info.lighter' : 'warning.lighter',
                                color: newService.isGraphical ? 'info.dark' : 'warning.dark'
                            }}
                        >
                            {newService.isGraphical ? "Graficzny" : "Tekstowy"}
                        </Typography>
                    </Box>
                </Box>

                {/* Dostępność materiałów */}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Dostępność materiałów w nowej usłudze
                </Typography>
                
                {/* Wyświetl w odpowiedniej kolejności w zależności od IsGraphical */}
                {newService.isGraphical ? (
                    <>
                        {graphicalTestsComponent}
                        {textualTestsComponent}
                    </>
                ) : (
                    <>
                        {textualTestsComponent}
                        {graphicalTestsComponent}
                    </>
                )}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Zmiana usługi bazowej</DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Czy na pewno chcesz zmienić usługę bazową z <strong>"{originalServiceName}"</strong> na <strong>"{newService.name}"</strong>?
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        Arkusz egzaminacyjny i klucz odpowiedzi zostaną pobrane z nowej usługi. Zmiany zostaną zastosowane po zapisaniu formularza.
                    </Typography>
                </Alert>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Dostępność materiałów w nowej usłudze:
                </Typography>
                {renderNewServiceAvailability()}

                {/* Sprawdź czy bieżące zamówienie ma już materiały testowe */}
                {(order.packageTestPath || order.packageTestKeyPath) && (
                    <Alert severity="warning" sx={{ mt: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            ⚠️ Bieżące zamówienie zawiera już materiały testowe. 
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={overwriteTestMaterials}
                                    onChange={(e) => setOverwriteTestMaterials(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Potwierdź nadpisanie materiałów z nowej usługi
                                </Typography>
                            }
                        />
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={handleCancel} 
                    variant="outlined"
                >
                    Anuluj
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained"
                    color="primary"
                >
                    Potwierdź zmianę
                </Button>
            </DialogActions>
        </Dialog>
    );
}